import { supabase } from "@/lib/supabase";
import type { OrganizationRow } from "@/types/database";

export type OrgStatus = OrganizationRow["org_status"];
export type OrgSalesStage = NonNullable<OrganizationRow["orgsales_stage"]>;
export type OrgClientStage = NonNullable<OrganizationRow["orgclient_stage"]>;

export interface ListOrgsFilters {
  org_status?: OrgStatus;
  orgsales_stage?: OrgSalesStage;
  orgclient_stage?: OrgClientStage;
}

export async function listOrganizations(
  filters?: ListOrgsFilters
): Promise<OrganizationRow[]> {
  let q = supabase.from("organizations").select("*").order("name", { ascending: true });
  if (filters?.org_status) {
    q = q.eq("org_status", filters.org_status);
  }
  if (filters?.orgsales_stage) {
    q = q.eq("orgsales_stage", filters.orgsales_stage);
  }
  if (filters?.orgclient_stage) {
    q = q.eq("orgclient_stage", filters.orgclient_stage);
  }
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as OrganizationRow[];
}

export async function getOrganization(id: string): Promise<OrganizationRow | null> {
  const { data, error } = await supabase
    .from("organizations")
    .select("*")
    .eq("id", id)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data as OrganizationRow;
}

export async function createOrganization(
  input: Record<string, unknown>
): Promise<OrganizationRow> {
  const { data, error } = await supabase
    .from("organizations")
    .insert(input as never)
    .select()
    .single();
  if (error) throw error;
  return data as OrganizationRow;
}

export async function updateOrganization(
  id: string,
  input: Record<string, unknown>
): Promise<OrganizationRow> {
  const { data, error } = await supabase
    .from("organizations")
    .update(input as never)
    .eq("id", id)
    .select()
    .maybeSingle();
  if (error) throw error;
  if (!data) {
    throw new Error(
      "Update did not return a row. Often this means Row Level Security blocked the update " +
        "(for example, the org admin UPDATE policy is missing on the remote database). " +
        "Apply the migration that adds `org_update_own_orgadmin` on `public.organizations`, " +
        "or confirm your user is an organization admin linked to this organization.",
    );
  }
  return data as OrganizationRow;
}

export async function deleteOrganization(id: string): Promise<void> {
  const { error } = await supabase.from("organizations").delete().eq("id", id);
  if (error) throw error;
}

/** Validation for stage transitions (Section 2 §3.4). Returns error message or null. */
export function validateStageTransition(
  _org: OrganizationRow,
  nextClientStage: OrgClientStage
): string | null {
  if (nextClientStage === "testing") {
    // Require min 1 employee, min 1 tag (checked in UI/API when we have counts)
    return null;
  }
  if (nextClientStage === "intervention") {
    // Require min 10 MAPS assessments (later)
    return null;
  }
  return null;
}
